import { z } from 'zod';

/**
 * Normalise a Vietnamese mobile number as a person types it ("0912 345 678",
 * "+84 912-345-678", "84912345678") to the national form `0912345678`, or
 * `null` when it is not a 10-digit VN number. Shared by the API (storage)
 * and the dashboard (inline validation), so both agree on what is accepted.
 *
 * Deliberately NOT `normalizeVnPhone` in `apps/api/src/services/zalo.ts`:
 * that one produces the `84…` form ZNS wants and also accepts a bare 9-digit
 * tail. The contact phone is something a human dials, so it stays in the
 * form a human writes.
 */
export function normalizeContactPhone(raw: string): string | null {
  let digits = (raw || '').replace(/\D/g, '');
  if (digits.startsWith('84') && digits.length === 11) digits = '0' + digits.slice(2);
  return /^0\d{9}$/.test(digits) ? digits : null;
}

/** The stored `0912345678` as a person reads it: `0912 345 678`. Anything
 *  not in the stored form is returned untouched. */
export function formatContactPhone(stored: string): string {
  return /^0\d{9}$/.test(stored) ? `${stored.slice(0, 4)} ${stored.slice(4, 7)} ${stored.slice(7)}` : stored;
}

/**
 * The org's contact phone as an API input: `null` clears it, a string is
 * normalised and must be a VN mobile number. Omitted (`undefined`) is left
 * to each route's partial-update semantics (INVARIANTS §4.1).
 */
export const contactPhoneSchema = z
  .string()
  .trim()
  .max(32)
  .transform((v, ctx) => {
    const n = normalizeContactPhone(v);
    if (n === null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Số điện thoại phải là số di động Việt Nam 10 chữ số' });
      return z.NEVER;
    }
    return n;
  })
  .nullable()
  .optional();
